import { useState, useCallback } from 'react';
import config from '../config/config';
import authHeader from '../config/authHeader';
import { useAuth } from '../contexts/AuthContext';

const ENDPOINTS = {
    fetch: '/4DACTION/KST_getLiensExtByOriAndUUID',
    create: '/4DACTION/KST_createLienExterne',
    update: '/4DACTION/KST_editLienExterne',
    delete: '/4DACTION/KST_deleteLienExterne',
};

const jsonHeaders = () => ({
    ...authHeader(),
    'Content-Type': 'application/json',
});

const useFetchLiensExternes = () => {
    const { logout } = useAuth();

    const [liensExts, setliensExts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mutating, setMutating] = useState(false);
    const [error, setError] = useState(null);

    // --- READ ---
    const fetchLiensExtsByUUIDAndOrigine = useCallback(async (uuid, origine) => {
        setLoading(true);
        setError(null);
        try {
            const url = `${config.apiBaseUrl}${ENDPOINTS.fetch}?uuid=${encodeURIComponent(uuid)}&origine=${encodeURIComponent(origine)}`;
            const response = await fetch(url, { method: 'GET', headers: authHeader() });

            if (response.status === 401) {
                logout();
                return null;
            }
            if (!response.ok) throw new Error('Erreur réseau pour récupérer les liens externes par UUID');

            const data = await response.json();
            setliensExts(Array.isArray(data) ? data : []);
            return data;
        } catch (err) {
            setError(err.message || String(err));
            throw err;
        } finally {
            setLoading(false);
        }
    }, [logout]);

    // --- CREATE ---
    const createLienExterne = useCallback(async (uuidExterne, origine, record) => {
        setMutating(true);
        setError(null);
        try {
            const payload = normalizeForDB({ ...record, UUID_Externe: uuidExterne, Origine: origine });
            const formData = new FormData();
            formData.append("LienExternePayload", JSON.stringify(payload));
            const response = await fetch(`${config.apiBaseUrl}${ENDPOINTS.create}`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) { logout(); return null; }
            if (!response.ok) throw new Error("Erreur réseau lors de la création du lien externe");

            const created = await response.json();
            setliensExts(prev => [...prev, created]);
            return created;
        } catch (err) {
            setError(err.message || String(err));
            throw err;
        } finally {
            setMutating(false);
        }
    }, [logout]);

    // --- UPDATE ---
    const updateLienExterne = useCallback(async (record) => {
        setMutating(true);
        setError(null);
        try {
            if (!record?.UUID_) throw new Error("UUID_ manquant pour la mise à jour");
            const payload = normalizeForDB(record);

            const formData = new FormData();
            formData.append("LienExternePayload", JSON.stringify(payload));

            const response = await fetch(`${config.apiBaseUrl}${ENDPOINTS.update}`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) { logout(); return null; }
            if (!response.ok) throw new Error("Erreur réseau lors de la mise à jour du lien externe");

            const updated = await response.json();
            setliensExts(prev => prev.map(l => (l.UUID_ === updated.UUID_ ? updated : l)));
            return updated;
        } catch (err) {
            setError(err.message || String(err));
            throw err;
        } finally {
            setMutating(false);
        }
    }, [logout]);

    // --- DELETE ---
    const deleteLienExterne = useCallback(async (uuid) => {
        setMutating(true);
        setError(null);
        try {
            if (!uuid) throw new Error("UUID_ requis pour supprimer");
            const response = await fetch(`${config.apiBaseUrl}${ENDPOINTS.delete}?uuid=${uuid}`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) { logout(); return null; }
            if (!response.ok) throw new Error("Erreur réseau lors de la suppression du lien externe");

            // 204/200 ok
            setliensExts(prev => prev.filter(l => l.UUID_ !== uuid));
            return true;
        } catch (err) {
            setError(err.message || String(err));
            throw err;
        } finally {
            setMutating(false);
        }
    }, [logout]);

    /**
     * Sauvegarde un "diff" à partir de la liste actuelle (liensExts) vers nextList :
     * - items SANS UUID_ => create
     * - items AVEC UUID_ et modifiés => update
     * - items supprimés (présents avant, absents après) => delete
     *
     * @param {string} uuidExterne
     * @param {string} origine
     * @param {Array} nextList  // format DB: {UUID_, UUID_Externe, Nom_Lien, Lien, Commentaires, Partage, Origine, Type_Lien, Creation}
     */
    const saveLiensExternesDiff = useCallback(async (uuidExterne, origine, nextList) => {
        setMutating(true);
        setError(null);
        try {
            const prev = Array.isArray(liensExts) ? liensExts : [];

            const prevById = new Map(prev.filter(x => !!x.UUID_).map(x => [x.UUID_, x]));
            const nextById = new Map((nextList || []).filter(x => !!x.UUID_).map(x => [x.UUID_, x]));

            // deletions: in prev but not in next
            const toDelete = [...prevById.keys()].filter(id => !nextById.has(id));

            // creations: in next without UUID_
            const toCreate = (nextList || []).filter(x => !x.UUID_);

            // updates: in next with UUID_ AND changed vs prev
            const toUpdate = (nextList || []).filter(x => {
                if (!x.UUID_) return false;
                const before = prevById.get(x.UUID_);
                return before ? !shallowEqualLink(before, x) : false;
            });

            // run in parallel (order: delete -> create -> update is not critical, but OK)
            const delPromises = toDelete.map(id => deleteLienExterne(id));
            const createPromises = toCreate.map(x => createLienExterne(uuidExterne, origine, x));
            const updatePromises = toUpdate.map(updateLienExterne);

            await Promise.allSettled([...delPromises, ...createPromises, ...updatePromises]);

            // Optionnel: rafraîchir depuis le serveur (source de vérité)
            await fetchLiensExtsByUUIDAndOrigine(uuidExterne, origine);
            return true;
        } catch (err) {
            setError(err.message || String(err));
            throw err;
        } finally {
            setMutating(false);
        }
    }, [liensExts, deleteLienExterne, createLienExterne, updateLienExterne, fetchLiensExtsByUUIDAndOrigine]);

    return {
        liensExts,
        fetchLiensExtsByUUIDAndOrigine,
        createLienExterne,
        updateLienExterne,
        deleteLienExterne,
        saveLiensExternesDiff,
        loading,
        mutating,
        error,
    };
};

export default useFetchLiensExternes;

/* ------------ helpers ------------- */

function shallowEqualLink(a = {}, b = {}) {
    return (
        (a.Nom_Lien || '') === (b.Nom_Lien || '') &&
        ensureHttp(a.Lien || '') === ensureHttp(b.Lien || '') &&
        (!!a.Partage) === (!!b.Partage) &&
        (a.Commentaires || '') === (b.Commentaires || '') &&
        (a.Origine || '') === (b.Origine || '') &&
        (normType(a.Type_Lien) || '') === (normType(b.Type_Lien) || '')
    );
}

function normalizeForDB(r = {}) {
    return {
        UUID_: r.UUID_ ?? null,
        UUID_Externe: r.UUID_Externe ?? null,
        Nom_Lien: (r.Nom_Lien || '').trim() || getDomain(r.Lien) || (r.Lien || ''),
        Lien: ensureHttp(r.Lien || ''),
        Commentaires: (r.Commentaires || '').trim(),
        Partage: !!r.Partage,
        Origine: (r.Origine || '').trim(),
        Type_Lien: normType(r.Type_Lien) || guessType(r.Lien),
        Creation: r.Creation ?? null,
    };
}

function ensureHttp(u) {
    if (!u) return '';
    return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}
function getDomain(u) {
    try { return new URL(ensureHttp(u)).hostname.replace(/^www\./, ''); } catch { return ''; }
}
function normType(t) {
    const x = (t || '').toLowerCase();
    if (['xls', 'xlsx', 'csv', 'sheet'].includes(x)) return 'sheet';
    if (['doc', 'docx', 'word'].includes(x)) return 'doc';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'image', 'img', 'webp'].includes(x)) return 'image';
    return x;
}
function guessType(url) {
    const u = (url || '').toLowerCase();
    if (u.includes('google.com/maps')) return 'map';
    if (u.includes('drive.google.com')) return 'drive';
    if (/\.(pdf)(\?|$)/.test(u)) return 'pdf';
    if (/\.(xlsx?|csv)(\?|$)/.test(u)) return 'sheet';
    if (/\.(docx?)(\?|$)/.test(u)) return 'doc';
    if (/\.(png|jpe?g|webp|gif|svg)(\?|$)/.test(u)) return 'image';
    return 'web';
}
